import React from 'react';
import template from 'lodash/template';
import { Dropdown } from '@glu/dropdown-react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Checkbox from 'components/checkbox/Checkbox';
import Input from 'components/gluValidation/Input';
import { FIELD_NAMES } from 'components/securityContact/securityContacts.constants';
import useLocale from 'components/useLocale/useLocale';
import { gt } from 'utils/validations/gluValidations';

import useStyles from './firstTimeLogin.styles';

const SecurityQuestionsForm = ({ availableQuestionsToPickFrom, onChange, values }) => {
  const classes = useStyles();
  const locale = useLocale();
  const securityValues = values.security || {};

  return (
    <div className={classes.wrapper}>
      <h2>{locale['security.securityQuestions']}</h2>
      <p>{locale['security.HeaderInfoline1']}</p>
      <h3>{locale['security.criteriaHeader']}</h3>
      <ul className={classnames(classes.criteriaList, classes.verticalGap)}>
        <li>{locale['security.HeaderInfoline2']}</li>
        <li>{locale['security.HeaderInfoline3']}</li>
        <li>{locale['security.HeaderInfoline4']}</li>
      </ul>

      <label className={classes.verticalGap} htmlFor="maskAnswers">
        <Checkbox
          checked={securityValues.maskAnswers}
          data-qa="maskAnswers"
          id="maskAnswers"
          name="maskAnswers"
          onChange={(e) =>
            onChange(FIELD_NAMES.SECURITY, { ...securityValues, maskAnswers: e.target.checked })
          }
        />
        <span className={classnames(classes.boldText, classes.checkboxText)}>
          {locale['common.maskAnswers']}
        </span>
      </label>
      <Dropdown
        className={classnames(classes.fullWidth, 'form-group')}
        dataQa="securityQuestion1"
        defaultValue={securityValues.securityQuestion1}
        filterEnabled={false}
        htmlId="securityQuestion1"
        labelText={locale['security.questions.question1']}
        name="securityQuestion1"
        options={availableQuestionsToPickFrom}
        validators={[{ required: true, msg: locale['validation.required'] }]}
      />
      <Input
        className={classnames(classes.fullWidth, 'form-group', classes.verticalGap)}
        data-qa="securityQuestionAnswer1"
        defaultValue={securityValues.securityQuestionAnswer1}
        label={locale['common.answer']}
        name="securityQuestionAnswer1"
        type={securityValues.maskAnswers ? 'password' : 'text'}
        validators={[
          { required: true, msg: locale['validation.required'] },
          {
            // Don't allow previously used answers
            fn: (name, _, data) => {
              const answer = data[name] ? String(data[name]).toLowerCase() : '';
              const answer2 = data.securityQuestionAnswer2
                ? String(data.securityQuestionAnswer2).toLowerCase()
                : '';
              const answer3 = data.securityQuestionAnswer3
                ? String(data.securityQuestionAnswer3).toLowerCase()
                : '';
              if ([answer2, answer3].includes(answer)) return false;
              return true;
            },
            msg: locale['security.questions.uniqueAnswers']
          },
          gt({ minLength: 3, msg: template(locale['validation.minNCharacters'])({ min: 3 }) })
        ]}
      />
      <Dropdown
        className={classnames(classes.fullWidth, 'form-group')}
        dataQa="securityQuestion2"
        defaultValue={securityValues.securityQuestion2}
        filterEnabled={false}
        htmlId="securityQuestion2"
        labelText={locale['security.questions.question2']}
        name="securityQuestion2"
        options={availableQuestionsToPickFrom}
        validators={[{ required: true, msg: locale['validation.required'] }]}
      />
      <Input
        className={classnames(classes.fullWidth, 'form-group', classes.verticalGap)}
        data-qa="securityQuestionAnswer2"
        defaultValue={securityValues.securityQuestionAnswer2}
        label={locale['common.answer']}
        name="securityQuestionAnswer2"
        type={securityValues.maskAnswers ? 'password' : 'text'}
        validators={[
          { required: true, msg: locale['validation.required'] },
          {
            // Don't allow previously used answers
            fn: (name, _, data) => {
              const answer = data[name] ? String(data[name]).toLowerCase() : '';
              const answer1 = data.securityQuestionAnswer1
                ? String(data.securityQuestionAnswer1).toLowerCase()
                : '';
              const answer3 = data.securityQuestionAnswer3
                ? String(data.securityQuestionAnswer3).toLowerCase()
                : '';
              if ([answer1, answer3].includes(answer)) return false;
              return true;
            },
            msg: locale['security.questions.uniqueAnswers']
          },
          gt({ minLength: 3, msg: template(locale['validation.minNCharacters'])({ min: 3 }) })
        ]}
      />
      <Dropdown
        className={classnames(classes.fullWidth, 'form-group')}
        dataQa="securityQuestion3"
        defaultValue={securityValues.securityQuestion3}
        filterEnabled={false}
        htmlId="securityQuestion3"
        labelText={locale['security.questions.question3']}
        name="securityQuestion3"
        options={availableQuestionsToPickFrom}
        validators={[{ required: true, msg: locale['validation.required'] }]}
      />
      <Input
        className={classnames(classes.fullWidth, 'form-group', classes.verticalGap)}
        data-qa="securityQuestionAnswer3"
        defaultValue={securityValues.securityQuestionAnswer3}
        label={locale['common.answer']}
        name="securityQuestionAnswer3"
        type={securityValues.maskAnswers ? 'password' : 'text'}
        validators={[
          { required: true, msg: locale['validation.required'] },
          {
            // Don't allow previously used answers
            fn: (name, _, data) => {
              const answer = data[name] ? String(data[name]).toLowerCase() : '';
              const answer2 = data.securityQuestionAnswer2
                ? String(data.securityQuestionAnswer2).toLowerCase()
                : '';
              const answer1 = data.securityQuestionAnswer1
                ? String(data.securityQuestionAnswer1).toLowerCase()
                : '';
              if ([answer2, answer1].includes(answer)) return false;
              return true;
            },
            msg: locale['security.questions.uniqueAnswers']
          },
          gt({ minLength: 3, msg: template(locale['validation.minNCharacters'])({ min: 3 }) })
        ]}
      />
    </div>
  );
};

SecurityQuestionsForm.propTypes = {
  availableQuestionsToPickFrom: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func.isRequired,
  values: PropTypes.shape({}).isRequired
};

export default SecurityQuestionsForm;
